// admin/src/pages/NoteList.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, TablePagination, Typography, Box, Button, TextField, Autocomplete, Skeleton
} from '@mui/material';
import { Edit, Delete, Clear } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { deleteNote, fetchNotes, fetchUsers } from '../services/api';
import Swal from 'sweetalert2';

type Note = {
  _id: string;
  slug?: string;
  title?: string;
  note?: string;
  entryDate?: string;
  datetime?: string;
  createdAt?: string;
  user?: any; // may be object or id
};

type UserOption = { _id: string; name: string };

const stripHtml = (html = '') => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const txt = div.textContent || div.innerText || '';
  return txt.replace(/\s+/g, ' ').trim();
};

const preview = (s: string, n = 140) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

const ensureDate = (n: Partial<Note>): Date | null => {
  const raw = n.entryDate || n.datetime || n.createdAt || '';
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const userLabel = (u: any): string => {
  if (!u) return '—';
  if (typeof u === 'string') return u;
  return (
    u.username ||
    u.fullName ||
    [u.firstName, u.lastName].filter(Boolean).join(' ') ||
    u.email ||
    u._id ||
    '—'
  );
};

// tiny debounce hook
const useDebounced = <T,>(val: T, ms = 250) => {
  const [d, setD] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setD(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return d;
};

const NoteList: React.FC = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(''); // yyyy-mm-dd
  const [loading, setLoading] = useState(true);

  // admin user filter
  const [users, setUsers] = useState<UserOption[]>([]);
  const [userSel, setUserSel] = useState<UserOption | null>(null);

  const dSearch = useDebounced(search);
  const dDate = useDebounced(date);
  const dUserSel = useDebounced(userSel);

  const navigate = useNavigate();

  const unwrap = (res: any): any[] => {
    if (!res) return [];
    // Axios: res.data is the payload; backend returns array
    const payload = Array.isArray(res) ? res : res.data ?? res;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
  };

  const loadUsers = async () => {
    try {
      const raw = await fetchUsers();
      const arr = unwrap(raw);
      const opts: UserOption[] = arr.map((u: any) => ({
        _id: u._id,
        name:
          u.username ||
          u.fullName ||
          [u.firstName, u.lastName].filter(Boolean).join(' ') ||
          u.email ||
          u._id,
      }));
      setUsers(opts);
    } catch (e) {
      console.warn('fetchUsers failed', e);
      setUsers([]);
    }
  };

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await fetchNotes({
        search: dSearch,
        date: dDate,
        adminAll: true,
        includeUser: true,
        userId: dUserSel?._id || undefined,
      });
      setNotes(unwrap(res) as Note[]);
    } catch (err) {
      console.error(err);
      setNotes([]);
      Swal.fire('Error', 'Failed to load notes', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dSearch, dDate, dUserSel]);

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You won’t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;

    try {
      await deleteNote(id);
      await loadNotes();
      Swal.fire('Deleted!', 'The note has been deleted.', 'success');
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Something went wrong while deleting the note.', 'error');
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pageRows = useMemo(
    () => notes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [notes, page, rowsPerPage]
  );

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={2}>
        <Typography variant="h5">All Users’ Notes</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/notes/create')}
          sx={{
            background: 'linear-gradient(to right, #6226A7, #F6A938)',
            color: '#fff',
            fontWeight: 600,
            '&:hover': { background: 'linear-gradient(to right, #4e1f84, #db8b21)' },
          }}
        >
          Add Note
        </Button>
      </Box>

      <Box display="flex" gap={2} mt={3} alignItems="center" flexWrap="wrap">
        <TextField
          label="Search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 250 }}
        />
        <TextField
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); setPage(0); }}
          sx={{ width: 200 }}
          label="Date"
          InputLabelProps={{ shrink: true }}
        />

        {/* Admin user filter (single-select) */}
        <Box sx={{ minWidth: 320, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Autocomplete<UserOption, false, false, false>
            options={users}
            value={userSel}
            onChange={(_e, v) => { setUserSel(v); setPage(0); }}
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            renderInput={(params) => <TextField {...params} label="Filter by user" placeholder="All users" />}
            clearOnEscape
          />
          {userSel && (
            <IconButton
              aria-label="clear user filter"
              onClick={() => { setUserSel(null); setPage(0); }}
              title="Clear"
            >
              <Clear />
            </IconButton>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#F6A938' }}>
            <TableRow>
              <TableCell><strong>User</strong></TableCell>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Note</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Time</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: Math.max(rowsPerPage, 5) }).map((_, idx) => (
                <TableRow key={idx}>
                  {[0, 1, 2, 3, 4, 5].map((cell) => (
                    <TableCell key={cell}>
                      <Skeleton height={18} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pageRows.length > 0 ? (
              pageRows.map((note) => {
                const d = ensureDate(note);
                const dateStr = d ? d.toLocaleDateString() : '—';
                const timeStr = d ? d.toLocaleTimeString() : '—';
                const txt = preview(stripHtml(note.note || ''), 140);

                return (
                  <TableRow key={note._id}>
                    <TableCell title={typeof note.user === 'object' ? (note.user?.email || note.user?._id || '') : ''}>
                      {userLabel(note.user)}
                    </TableCell>
                    <TableCell>{note.title || '—'}</TableCell>
                    <TableCell title={stripHtml(note.note || '')}>{txt || '—'}</TableCell>
                    <TableCell>{dateStr}</TableCell>
                    <TableCell>{timeStr}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => navigate(`/notes/edit/${note.slug || note._id}`)} aria-label="Edit">
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(note.slug || note._id)} aria-label="Delete">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">No notes found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {notes.length > 0 && (
          <TablePagination
            component="div"
            count={notes.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>
    </>
  );
};

export default NoteList;
